import React, { useEffect, useMemo, useState } from 'react';
import QueryString from 'query-string';
import { useQuery, useMutation } from '@apollo/client/react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ServerError from '../error/ServerError';
import Loading from '../../components/shared/Loading';
import Page from '../Page';
import {
  CREATE_MEAL,
  DELETE_MEAL,
  DUPLICATE_MEAL,
  GET_MEALS,
} from '../../graphql/nutrition';
import config from '../../config';

const LIMIT = 100;
const pageSizeOptions = [12, 24, 48];
const categoryTabs = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert'];

const statusLabels = {
  DRAFT: 'Draft',
  FREE: 'Published',
  PREMIUM: 'Premium',
};

const statusStyles = {
  DRAFT: { color: '#f97316', bg: '#fff7ed' },
  FREE: { color: '#16a34a', bg: '#dcfce7' },
  PREMIUM: { color: '#4338ca', bg: '#ebe9ff' },
};

const secondsToMinutes = (seconds) =>
  seconds ? Math.max(1, Math.round(seconds / 60)) : null;

const getMealTime = (meal) =>
  secondsToMinutes(meal.totalTimeSeconds) ||
  secondsToMinutes((meal.prepTimeSeconds || 0) + (meal.cookTimeSeconds || 0));

const getMealType = (meal) => {
  const tags = meal.tags || [];
  return tags.find((tag) =>
    categoryTabs.some((category) => category.toLowerCase() === tag.toLowerCase())
  );
};

const hasMacros = (meal) =>
  ['calories', 'proteinGrams', 'carbGrams', 'fatGrams'].some(
    (key) => meal.macros?.[key] != null
  );

const formatMacro = (value, suffix = '') =>
  value == null || Number.isNaN(Number(value)) ? '-' : `${Math.round(value)}${suffix}`;

const caloriesMatch = (meal, filter) => {
  const calories = meal.macros?.calories;
  if (filter === 'ALL') return true;
  if (calories == null) return false;
  if (filter === 'LT_300') return calories < 300;
  if (filter === '300_500') return calories >= 300 && calories < 500;
  if (filter === '500_700') return calories >= 500 && calories < 700;
  if (filter === 'GT_700') return calories >= 700;
  return true;
};

const StatCard = ({ icon, value, label, detail, color, backgroundColor }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ py: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: backgroundColor,
            borderRadius: 2,
            color,
            display: 'flex',
            height: 48,
            justifyContent: 'center',
            width: 48,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>{label}</Typography>
          {detail && (
            <Typography sx={{ color, fontSize: 13, fontWeight: 700, mt: 0.4 }}>
              {detail}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const NoMealImage = () => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      color: '#64748b',
      display: 'flex',
      fontSize: 13,
      fontWeight: 700,
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    }}
  >
    No cover image
  </Box>
);

const MealCard = ({ meal, onDelete, onDuplicate }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const statusStyle = statusStyles[meal.status] || statusStyles.DRAFT;
  const mealType = getMealType(meal);
  const minutes = getMealTime(meal);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: '#e2e8f0',
        borderRadius: 1,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 170, position: 'relative', bgcolor: '#f1f5f9' }}>
        {meal.coverImageUrl ? (
          <Box
            component="img"
            src={meal.coverImageUrl}
            alt={meal.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <NoMealImage />
        )}
        <Chip
          size="small"
          label={statusLabels[meal.status] || meal.status || 'Draft'}
          sx={{
            bgcolor: statusStyle.bg,
            color: statusStyle.color,
            fontWeight: 800,
            position: 'absolute',
            right: 12,
            top: 12,
            textTransform: 'uppercase',
          }}
        />
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 1 } }}>
        <Typography noWrap sx={{ color: '#0f172a', fontSize: 18, fontWeight: 800, mb: 0.75 }}>
          {meal.title || 'Untitled meal'}
        </Typography>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ color: '#40536b', mb: 1.5 }}>
          {mealType && (
            <Typography sx={{ color: '#2563eb', fontSize: 14, fontWeight: 700 }}>
              {mealType}
            </Typography>
          )}
          {mealType && <Typography>•</Typography>}
          <Typography sx={{ fontSize: 14 }}>{minutes ? `${minutes} min` : '-'}</Typography>
          <Typography>•</Typography>
          <Typography sx={{ fontSize: 14 }}>
            {meal.recipeIngredients?.length
              ? `${meal.recipeIngredients.length} ingredients`
              : '-'}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2.5} sx={{ mb: 2 }}>
          <Typography sx={{ color: '#263a59', fontSize: 14 }}>
            {formatMacro(meal.macros?.calories, ' kcal')}
          </Typography>
          <Typography sx={{ color: '#263a59', fontSize: 14 }}>
            {formatMacro(meal.macros?.proteinGrams, 'g P')}
          </Typography>
          <Typography sx={{ color: '#263a59', fontSize: 14 }}>
            {formatMacro(meal.macros?.carbGrams, 'g C')}
          </Typography>
          <Typography sx={{ color: '#263a59', fontSize: 14 }}>
            {formatMacro(meal.macros?.fatGrams, 'g F')}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ borderTop: '1px solid #e2e8f0', pt: 1.25 }}>
          <Button
            component={RouterLink}
            to={`/trainer/meals/${meal._id}`}
            startIcon={<EditOutlinedIcon />}
            sx={{ flex: 1 }}
          >
            Edit
          </Button>
          <Button
            component={RouterLink}
            to={`/trainer/meals/${meal._id}/preview`}
            startIcon={<VisibilityOutlinedIcon />}
            sx={{ flex: 1, color: '#17213f' }}
          >
            Preview
          </Button>
          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ border: '1px solid #d0d9e6', borderRadius: 1, height: 40, width: 40 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onDuplicate(meal);
              }}
            >
              <FileCopyOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
              Duplicate
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onDelete(meal);
              }}
            >
              <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          </Menu>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function MealList() {
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [tagFilter, setTagFilter] = useState('ALL');
  const [calorieFilter, setCalorieFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);

  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, data, refetch, fetchMore } = useQuery(GET_MEALS, {
    variables: { input: { trainerId, searchTerm: searchQuery, limit: LIMIT } },
  });
  const [createMeal] = useMutation(CREATE_MEAL);
  const [duplicateMeal] = useMutation(DUPLICATE_MEAL);
  const [deleteMeal] = useMutation(DELETE_MEAL);

  useEffect(() => {
    refetch();
  }, [refetch, location]);

  const meals = data?.meals || [];
  const tagOptions = useMemo(
    () =>
      Array.from(new Set(meals.flatMap((meal) => meal.tags || []).filter(Boolean))).sort(),
    [meals]
  );
  const filteredMeals = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return meals
      .filter((meal) => {
        const matchesSearch =
          !normalizedSearch ||
          (meal.title || '').toLowerCase().includes(normalizedSearch) ||
          (meal.desc || '').toLowerCase().includes(normalizedSearch);
        const matchesStatus =
          statusFilter === 'ALL' || meal.status === statusFilter;
        const matchesType =
          typeFilter === 'ALL' ||
          (getMealType(meal) || '').toLowerCase() === typeFilter.toLowerCase();
        const matchesTag =
          tagFilter === 'ALL' || (meal.tags || []).includes(tagFilter);
        const matchesCalories = caloriesMatch(meal, calorieFilter);

        return (
          matchesSearch &&
          matchesStatus &&
          matchesType &&
          matchesTag &&
          matchesCalories
        );
      })
      .sort((a, b) => {
        const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
        if (sortBy === 'oldest') return aDate - bDate;
        if (sortBy === 'calories') {
          return (b.macros?.calories || 0) - (a.macros?.calories || 0);
        }
        return bDate - aDate;
      });
  }, [calorieFilter, meals, searchQuery, sortBy, statusFilter, tagFilter, typeFilter]);

  useEffect(() => {
    setPage(1);
  }, [calorieFilter, perPage, searchQuery, sortBy, statusFilter, tagFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMeals.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart = filteredMeals.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const pageEnd = Math.min(currentPage * perPage, filteredMeals.length);
  const paginatedMeals = filteredMeals.slice(pageStart - 1, pageEnd);
  const draftCount = meals.filter((meal) => meal.status === 'DRAFT').length;
  const publishedCount = meals.filter((meal) => meal.status === 'FREE').length;
  const withMacrosCount = meals.filter(hasMacros).length;
  const averageCalories = withMacrosCount
    ? Math.round(
        meals.reduce((sum, meal) => sum + (meal.macros?.calories || 0), 0) /
          withMacrosCount
      )
    : null;

  const handleCreate = (event) => {
    event.preventDefault();
    createMeal({
      variables: { input: { title: 'Untitled' } },
    }).then((r) => {
      if (r.data?.createMeal?._id) {
        navigate(`/trainer/meals/${r.data.createMeal._id}`);
      }
    });
  };

  const handleLoadMore = async (event) => {
    event.preventDefault();
    const lastId = meals[meals.length - 1]?._id;
    if (!lastId) return;

    await fetchMore({
      variables: {
        input: {
          trainerId,
          searchTerm: searchQuery,
          limit: LIMIT,
          lastId,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => ({
        meals: [...previousResult.meals, ...fetchMoreResult.meals],
      }),
    });
  };

  const handleDelete = (meal) => {
    if (!window.confirm('Delete this meal?')) return;
    deleteMeal({ variables: { id: meal._id } }).then(() => refetch());
  };

  const handleDuplicate = (meal) => {
    duplicateMeal({ variables: { id: meal._id } }).then((r) => {
      if (r.data?.duplicateMeal?._id) {
        navigate(`/trainer/meals/${r.data.duplicateMeal._id}`);
      }
    });
  };

  if (error) return <ServerError message="Failed to load" />;
  if (loading || !data?.meals) return <Loading />;

  return (
    <Page title={`Meals : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#40536b', fontSize: 14, mb: 1 }}>
            Nutrition &nbsp;›&nbsp; <Box component="span" sx={{ color: '#4338ca' }}>Meals</Box>
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ color: '#071120', fontSize: 30, fontWeight: 800 }}>
                Meals
              </Typography>
              <Typography sx={{ color: '#40536b', fontSize: 16, mt: 0.75 }}>
                Create and manage meals for your clients. Add ingredients, recipes, and nutrition info.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                onClick={handleCreate}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: '#4338ca',
                  px: 2.5,
                  '&:hover': { bgcolor: '#3730a3' },
                }}
                variant="contained"
              >
                Create New Meal
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2 }}>
          <Grid item xs={12} md={2.4}>
            <StatCard
              icon={<RestaurantMenuIcon />}
              value={meals.length}
              label="Total Meals"
              detail={`${filteredMeals.length} matching`}
              color="#4338ca"
              backgroundColor="#edeaff"
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              icon={<EditOutlinedIcon />}
              value={draftCount}
              label="Drafts"
              detail="Continue editing"
              color="#f97316"
              backgroundColor="#fff0df"
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              icon={<CheckCircleOutlineIcon />}
              value={publishedCount}
              label="Published"
              detail="Ready to use"
              color="#16a34a"
              backgroundColor="#dcfce7"
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              icon={<LocalDiningIcon />}
              value={withMacrosCount}
              label="With Macros"
              detail="Nutrition entered"
              color="#2563eb"
              backgroundColor="#dbeafe"
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              icon={<WhatshotIcon />}
              value={averageCalories == null ? '-' : averageCalories.toLocaleString()}
              label="Avg. Calories"
              detail="Per meal"
              color="#8b5cf6"
              backgroundColor="#f0e7ff"
            />
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{ mb: 2 }}
            >
              <TextField
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search meals..."
                size="small"
                sx={{ minWidth: { xs: '100%', lg: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#40536b' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Select
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setStatusFilter(event.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
                value={statusFilter}
              >
                <MenuItem value="ALL">Status: All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="FREE">Published</MenuItem>
                <MenuItem value="PREMIUM">Premium</MenuItem>
              </Select>
              <Select
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setTypeFilter(event.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
                value={typeFilter}
              >
                <MenuItem value="ALL">Meal Type: All</MenuItem>
                {categoryTabs.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
              <Select
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setTagFilter(event.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
                value={tagFilter}
              >
                <MenuItem value="ALL">Tag: All</MenuItem>
                {tagOptions.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
              <Select
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setCalorieFilter(event.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
                value={calorieFilter}
              >
                <MenuItem value="ALL">Calories: Any</MenuItem>
                <MenuItem value="LT_300">Less than 300</MenuItem>
                <MenuItem value="300_500">300-500</MenuItem>
                <MenuItem value="500_700">500-700</MenuItem>
                <MenuItem value="GT_700">700+</MenuItem>
              </Select>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('ALL');
                  setTypeFilter('ALL');
                  setTagFilter('ALL');
                  setCalorieFilter('ALL');
                }}
                startIcon={<FilterAltOutlinedIcon />}
                variant="outlined"
              >
                Reset
              </Button>
              <Select
                IconComponent={KeyboardArrowDownIcon}
                onChange={(event) => setSortBy(event.target.value)}
                size="small"
                startAdornment={<SortIcon sx={{ color: '#40536b', mr: 1 }} />}
                sx={{ minWidth: 190 }}
                value={sortBy}
              >
                <MenuItem value="newest">Sort: Newest</MenuItem>
                <MenuItem value="oldest">Sort: Oldest</MenuItem>
                <MenuItem value="calories">Sort: Calories</MenuItem>
              </Select>
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
              <Button
                variant={typeFilter === 'ALL' ? 'contained' : 'outlined'}
                onClick={() => setTypeFilter('ALL')}
                sx={{ boxShadow: 'none' }}
              >
                All Meals
              </Button>
              {categoryTabs.map((category) => (
                <Button
                  key={category}
                  variant={typeFilter === category ? 'contained' : 'outlined'}
                  onClick={() => setTypeFilter(category)}
                  sx={{ boxShadow: 'none' }}
                >
                  {category}
                </Button>
              ))}
            </Stack>

            <Grid container spacing={3}>
              {paginatedMeals.map((meal) => (
                <Grid item xs={12} sm={6} lg={3} key={meal._id}>
                  <MealCard
                    meal={meal}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                  />
                </Grid>
              ))}
            </Grid>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Typography sx={{ color: '#40536b', fontSize: 14 }}>
                Showing {pageStart} to {pageEnd} of {filteredMeals.length} meals
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button
                  disabled={currentPage <= 1}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  variant="outlined"
                >
                  ‹
                </Button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <Button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    variant={pageNumber === currentPage ? 'contained' : 'text'}
                    sx={{ minWidth: 38 }}
                  >
                    {pageNumber}
                  </Button>
                ))}
                <Button
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  variant="outlined"
                >
                  ›
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography sx={{ color: '#40536b', fontSize: 14 }}>Show</Typography>
                <Select
                  size="small"
                  value={perPage}
                  onChange={(event) => setPerPage(Number(event.target.value))}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
                <Typography sx={{ color: '#40536b', fontSize: 14 }}>per page</Typography>
              </Stack>
            </Stack>

            {meals.length > 1 && meals.length >= LIMIT && (
              <Box mt={2}>
                <Button variant="outlined" onClick={handleLoadMore}>
                  Load more from server
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}
